import React, { useState, useEffect } from 'react';
import { Play, Mail, Send, Check, ArrowRight, Video, Zap, Clock, Star, Film, Scissors, Sparkles, Users, Trophy, MessageCircle, Package, CalendarClock, Upload, Edit3, Rocket, X, Camera, CheckCircle } from 'lucide-react';
import { portfolioItems, services, whyWorkWithMe, processSteps, productionSteps } from '../mock';

const Home = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isVisible, setIsVisible] = useState(false);
  const [videoModal, setVideoModal] = useState({ isOpen: false, videoId: null, title: '' });
  const [rotatingText, setRotatingText] = useState(0);

  const rotatingPhrases = ['moves people.', 'drives action.', 'stops the scroll.', 'gets results.'];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setRotatingText((prev) => (prev + 1) % rotatingPhrases.length);
    }, 2500); // Change every 2.5 seconds

    return () => clearInterval(interval);
  }, [rotatingPhrases.length]);

  const categories = ['All', 'Reels / Shorts', 'Events', 'Cinematic', 'Commercial'];

  const filteredPortfolio = selectedCategory === 'All' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === selectedCategory);

  const scrollToPortfolio = () => {
    document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const openVideoModal = (videoId, title) => {
    if (videoId) {
      setVideoModal({ isOpen: true, videoId, title });
      document.body.style.overflow = 'hidden';
    }
  };

  const closeVideoModal = () => {
    setVideoModal({ isOpen: false, videoId: null, title: '' });
    document.body.style.overflow = 'unset';
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="hero-gradient-bg" />
        <div className="hero-grain" />
        
        {/* Logo Brand */}
        <div className="absolute top-8 left-1/2 -translate-x-1/2 z-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_4287873c-d9ef-49d6-abf7-3886efd8bdcd/artifacts/ii3hsw4d_Freiberghini%20Visuals%20V3-white.png"
            alt="Freiberghini Visuals"
            className="h-16 md:h-20 w-auto opacity-90"
          />
        </div>

        {/* Hero Content */}
        <div className={`hero-content relative z-10 max-w-5xl mx-auto px-6 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="hero-headline mb-6">
            Video production & editing that actually <span className="hero-accent">drives action.</span>
          </h1>
          <p className="hero-subheadline mb-10">
            One creator. Full video solution. Built for today's platforms.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={scrollToPortfolio} className="cta-button-primary group">
              View Work
              <ArrowRight className="inline-block ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={scrollToContact} className="cta-button-secondary">
              Hire Me
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 border-2 border-yellow-400/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-zinc-950">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-8 h-8 text-yellow-400" />
            <h2 className="section-heading text-center">What I Do</h2>
          </div>
          <p className="text-center text-gray-400 mb-12 text-lg">Video production, editing, and supporting creative work.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => {
              const icons = [Scissors, Film, Video, Package, Edit3, Clock];
              const Icon = icons[index % icons.length];
              return (
                <div 
                  key={index} 
                  className="service-card group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-start gap-4">
                    <div className="service-icon">
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="text-lg text-gray-200 flex-1">{service}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-center text-yellow-400 mt-8 text-lg font-medium">
            Got something specific? I adapt fast.
          </p>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Film className="w-8 h-8 text-yellow-400" />
            <h2 className="section-heading text-center">Portfolio</h2>
          </div>
          <p className="text-center text-gray-400 mb-10 text-lg">Selected work across formats</p>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`category-button ${
                  selectedCategory === category ? 'active' : ''
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Portfolio Grid */}
          <div className="portfolio-grid">
            {filteredPortfolio.map((item, index) => (
              <div
                key={item.id} 
                className="portfolio-card group"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => openVideoModal(item.videoId, item.title)}
              >
                <div className="portfolio-thumbnail">
                  <img 
                    src={item.thumbnail} 
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="portfolio-overlay">
                    <button className="play-button">
                      <Play className="w-8 h-8 fill-current" />
                    </button>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                    <Video className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-1" />
                  </div>
                  <p className="text-sm text-gray-400 mb-3">{item.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="inline-block text-xs uppercase tracking-wider text-yellow-400 font-medium">
                      {item.category}
                    </span>
                    {item.videoId && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        <span>Featured</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Work With Me */}
      <section className="py-20 px-6 bg-zinc-950">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Trophy className="w-8 h-8 text-yellow-400" />
            <h2 className="section-heading text-center">Why Work With Me</h2>
          </div>
          <p className="text-center text-gray-400 mb-12 text-lg">Proven quality, reliable delivery</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {whyWorkWithMe.map((point, index) => {
              const icons = [MessageCircle, Clock, Trophy, Users, Package, Zap];
              const Icon = icons[index % icons.length];
              return (
                <div 
                  key={index} 
                  className="why-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Icon className="w-6 h-6 text-yellow-400 mb-3" />
                  <p className="text-lg text-gray-200">{point}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section - Editing Only */}
      <section className="process-section py-24 px-6 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Edit3 className="w-8 h-8 text-yellow-400" />
            <h2 className="section-heading text-center">Simple Editing Process</h2>
          </div>
          <p className="text-center text-gray-400 mb-16 text-lg">When you already have footage</p>
          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => {
              const icons = [Upload, Edit3, CheckCircle];
              const Icon = icons[index];
              return (
                <div 
                  key={index} 
                  className="process-card"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <div className="process-icon-wrapper">
                    <Icon className="w-12 h-12 text-yellow-400" />
                  </div>
                  <div className="process-number">{step.number}</div>
                  <h3 className="text-2xl font-medium text-white mb-3">{step.title}</h3>
                  <p className="text-gray-400 text-lg leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Spacer for section transition */}
      <div className="section-spacer"></div>

      {/* Process Section - Full Production */}
      <section className="process-section py-24 px-6 bg-zinc-950">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Camera className="w-8 h-8 text-yellow-400" />
            <h2 className="section-heading text-center">Full Video Production Process</h2>
          </div>
          <p className="text-center text-gray-400 mb-16 text-lg">From idea to final video</p>
          <div className="grid md:grid-cols-3 gap-8">
            {productionSteps.map((step, index) => {
              const icons = [MessageCircle, Camera, CheckCircle];
              const Icon = icons[index];
              return (
                <div 
                  key={index} 
                  className="process-card"
                  style={{ animationDelay: `${index * 0.15}s` }}
                >
                  <div className="process-icon-wrapper">
                    <Icon className="w-12 h-12 text-yellow-400" />
                  </div>
                  <div className="process-number">{step.number}</div>
                  <h3 className="text-2xl font-medium text-white mb-3">{step.title}</h3>
                  <p className="text-gray-400 text-lg leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Spacer for section transition */}
      <div className="section-spacer-reverse"></div>

      {/* Availability CTA */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-3xl mx-auto text-center">
          <div className="availability-badge">
            <Star className="w-4 h-4 fill-current" />
            <span>Available for New Projects</span>
          </div>
          <h2 className="cta-headline mb-8">
            Ready to create something that stands out?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={scrollToContact} className="cta-button-primary group">
              Let's Work
              <Zap className="inline-block ml-2 w-4 h-4" />
            </button>
            <button onClick={scrollToContact} className="cta-button-secondary">
              Contact Me
            </button>
          </div>
        </div>
      </section>

      {/* Spacer for section transition */}
      <div className="section-spacer"></div>

      {/* Contact */}
      <section id="contact" className="py-20 px-6 bg-zinc-950">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="section-heading mb-12">Get In Touch</h2>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a 
              href="mailto:freiberghini@gmail.com" 
              className="contact-link group"
            >
              <Mail className="w-5 h-5" />
              <span>freiberghini@gmail.com</span>
            </a>
            <a 
              href="https://instagram.com/freiberghini" 
              target="_blank" 
              rel="noopener noreferrer"
              className="contact-link group"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <span>@freiberghini</span>
            </a>
            <a 
              href="https://t.me/freiberghini" 
              target="_blank" 
              rel="noopener noreferrer"
              className="contact-link group"
            >
              <Send className="w-5 h-5" />
              <span>@freiberghini</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-section">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="footer-simple-content">
            <p className="footer-copyright">
              © 2025 Freiberghini Visuals. All rights reserved.
            </p>
            <div className="footer-badge">
              <Video className="w-4 h-4" />
              <span>10+ Years Experience</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      {videoModal.isOpen && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" onClick={closeVideoModal}>
              <X className="w-6 h-6" />
            </button>
            <div className="video-modal-header">
              <h3 className="text-xl font-semibold text-white">{videoModal.title}</h3>
            </div>
            <div className="video-modal-player">
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${videoModal.videoId}?autoplay=1`}
                title={videoModal.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
